// Minimal PSBT (BIP-174) editor focused on appending an OP_RETURN output.
//
// A PSBT is structured as:
//   magic (0x70736274ff)
//   global key-value map (contains the unsigned tx under key 0x00)
//   per-input key-value maps
//   per-output key-value maps
//
// To add an OP_RETURN output we must:
//   1. Append a new output (scriptPubKey = OP_RETURN <data>, value = 0) to the
//      unsigned transaction inside the global map.
//   2. Append a new (empty) per-output key-value map so input/output counts match.
//
// This does NOT support PSBTs that are already partially signed in a way that
// would be invalidated — adding an output changes the txid, so any existing
// signatures become invalid. That's expected: you add the OP_RETURN BEFORE
// signing in Sparrow.

import { Buffer } from "buffer";

const PSBT_MAGIC = Buffer.from([0x70, 0x73, 0x62, 0x74, 0xff]);
const OP_RETURN = 0x6a;
const OP_PUSHDATA1 = 0x4c;
const OP_PUSHDATA2 = 0x4d;

/**
 * Max OP_RETURN payload size this tool allows. Bitcoin Core v30 raised the
 * standard relay limit substantially (removed the old 80-byte datacarrier cap).
 * We allow up to 500 bytes here.
 */
export const MAX_OP_RETURN_BYTES = 500;

export class PsbtError extends Error {}

/** Simple forward-reading byte cursor. */
class Reader {
  buf: Buffer;
  pos = 0;
  constructor(buf: Buffer) {
    this.buf = buf;
  }
  get remaining(): number {
    return this.buf.length - this.pos;
  }
  readBytes(n: number): Buffer {
    if (this.pos + n > this.buf.length) {
      throw new PsbtError("Unexpected end of data (corrupted PSBT?).");
    }
    const out = this.buf.subarray(this.pos, this.pos + n);
    this.pos += n;
    return Buffer.from(out);
  }
  readUInt8(): number {
    return this.readBytes(1)[0];
  }
  readUInt32LE(): number {
    const b = this.readBytes(4);
    return b.readUInt32LE(0);
  }
  readUInt64LE(): bigint {
    const b = this.readBytes(8);
    return b.readBigUInt64LE(0);
  }
  /** Bitcoin compact size / varint. */
  readVarInt(): number {
    const first = this.readUInt8();
    if (first < 0xfd) return first;
    if (first === 0xfd) return this.readBytes(2).readUInt16LE(0);
    if (first === 0xfe) return this.readBytes(4).readUInt32LE(0);
    // 0xff -> 8 bytes; realistically never needed for counts here
    const big = this.readBytes(8).readBigUInt64LE(0);
    return Number(big);
  }
}

function writeVarInt(n: number): Buffer {
  if (n < 0xfd) return Buffer.from([n]);
  if (n <= 0xffff) {
    const b = Buffer.alloc(3);
    b[0] = 0xfd;
    b.writeUInt16LE(n, 1);
    return b;
  }
  if (n <= 0xffffffff) {
    const b = Buffer.alloc(5);
    b[0] = 0xfe;
    b.writeUInt32LE(n, 1);
    return b;
  }
  const b = Buffer.alloc(9);
  b[0] = 0xff;
  b.writeBigUInt64LE(BigInt(n), 1);
  return b;
}

/** Reads a key-value map until the 0x00 separator. Returns raw bytes consumed. */
function readKeyValueMap(r: Reader): Buffer {
  const start = r.pos;
  for (;;) {
    const keyLen = r.readVarInt();
    if (keyLen === 0) {
      // separator consumed
      break;
    }
    r.readBytes(keyLen); // key
    const valLen = r.readVarInt();
    r.readBytes(valLen); // value
  }
  return r.buf.subarray(start, r.pos);
}

interface ParsedTx {
  version: Buffer; // 4 bytes
  hasWitness: boolean;
  inputs: Buffer; // raw serialized inputs incl. count varint
  outputsCount: number;
  outputs: Buffer; // raw serialized outputs (NO count varint), concatenated
  locktime: Buffer; // 4 bytes
  witness: Buffer; // raw witness data if segwit (empty if not)
}

/**
 * Parse an unsigned (or signed) legacy/segwit transaction enough to splice in a
 * new output. The unsigned tx in a PSBT global map must be non-witness per
 * BIP-174, so witness handling is defensive only.
 */
function parseTx(r: Reader): ParsedTx {
  const version = r.readBytes(4);

  let hasWitness = false;
  // peek for segwit marker 0x00 0x01
  const markerPos = r.pos;
  const marker = r.readUInt8();
  const flag = r.readUInt8();
  if (marker === 0x00 && flag === 0x01) {
    hasWitness = true;
  } else {
    r.pos = markerPos; // rewind
  }

  // inputs
  const inStart = r.pos;
  const inCount = r.readVarInt();
  for (let i = 0; i < inCount; i++) {
    r.readBytes(32); // prev txid
    r.readBytes(4); // vout
    const scriptLen = r.readVarInt();
    r.readBytes(scriptLen); // scriptSig
    r.readBytes(4); // sequence
  }
  const inputs = Buffer.from(r.buf.subarray(inStart, r.pos));

  // outputs
  const outCount = r.readVarInt();
  const outStart = r.pos;
  for (let i = 0; i < outCount; i++) {
    r.readBytes(8); // value
    const scriptLen = r.readVarInt();
    r.readBytes(scriptLen);
  }
  const outputs = Buffer.from(r.buf.subarray(outStart, r.pos));

  let witness = Buffer.alloc(0);
  if (hasWitness) {
    const wStart = r.pos;
    for (let i = 0; i < inCount; i++) {
      const stackItems = r.readVarInt();
      for (let j = 0; j < stackItems; j++) {
        const len = r.readVarInt();
        r.readBytes(len);
      }
    }
    witness = Buffer.from(r.buf.subarray(wStart, r.pos));
  }

  const locktime = r.readBytes(4);

  return {
    version,
    hasWitness,
    inputs,
    outputsCount: outCount,
    outputs,
    locktime,
    witness,
  };
}

function serializeTx(tx: ParsedTx): Buffer {
  const parts: Buffer[] = [tx.version];
  if (tx.hasWitness) {
    parts.push(Buffer.from([0x00, 0x01]));
  }
  parts.push(tx.inputs);
  parts.push(writeVarInt(tx.outputsCount));
  parts.push(tx.outputs);
  if (tx.hasWitness) {
    parts.push(tx.witness);
  }
  parts.push(tx.locktime);
  return Buffer.concat(parts);
}

/** Build an OP_RETURN scriptPubKey from raw data bytes. */
export function buildOpReturnScript(data: Buffer): Buffer {
  if (data.length > MAX_OP_RETURN_BYTES) {
    throw new PsbtError(
      `OP_RETURN data is too large: ${data.length} bytes (maximum ${MAX_OP_RETURN_BYTES} bytes).`,
    );
  }
  // OP_RETURN, then a push of the data using the smallest valid push opcode.
  if (data.length < OP_PUSHDATA1) {
    // Direct push: single length byte (1..75) + data.
    return Buffer.concat([Buffer.from([OP_RETURN, data.length]), data]);
  }
  if (data.length <= 0xff) {
    // 76..255 bytes: OP_PUSHDATA1 + 1 length byte + data.
    return Buffer.concat([
      Buffer.from([OP_RETURN, OP_PUSHDATA1, data.length]),
      data,
    ]);
  }
  // 256..500 bytes: OP_PUSHDATA2 + 2-byte little-endian length + data.
  const lenBytes = Buffer.alloc(2);
  lenBytes.writeUInt16LE(data.length, 0);
  return Buffer.concat([
    Buffer.from([OP_RETURN, OP_PUSHDATA2]),
    lenBytes,
    data,
  ]);
}

/** Serialize a single tx output (8-byte value + varint scriptLen + script). */
function serializeTxOutput(valueSats: bigint, script: Buffer): Buffer {
  const value = Buffer.alloc(8);
  value.writeBigUInt64LE(valueSats, 0);
  return Buffer.concat([value, writeVarInt(script.length), script]);
}

/** Quick check that a base64 string looks like a PSBT. */
export function isLikelyPsbt(base64: string): boolean {
  try {
    const buf = Buffer.from(base64.trim(), "base64");
    return buf.subarray(0, 5).equals(PSBT_MAGIC);
  } catch {
    return false;
  }
}

export type TxInputFormat = "psbt" | "rawhex" | "unknown";

/** Normalize hex: trim, strip whitespace and optional 0x prefix, lowercase. */
function normalizeHex(input: string): string {
  let hex = input.trim().replace(/\s+/g, "").toLowerCase();
  if (hex.startsWith("0x")) hex = hex.slice(2);
  return hex;
}

/** Detect whether the pasted text is a Base64 PSBT or a raw hex transaction. */
export function detectFormat(input: string): TxInputFormat {
  const trimmed = input.trim();
  if (!trimmed) return "unknown";

  // Base64 PSBTs always start with "cHNidP" (the magic 0x70736274 in base64).
  if (trimmed.startsWith("cHNidP") || isLikelyPsbt(trimmed)) {
    return "psbt";
  }

  // Raw hex transaction: only hex chars, even length, starts with a plausible
  // version (0x01000000 or 0x02000000 little-endian => "01000000"/"02000000").
  const hex = normalizeHex(trimmed);
  if (hex.length >= 20 && hex.length % 2 === 0 && /^[0-9a-f]+$/.test(hex)) {
    return "rawhex";
  }

  return "unknown";
}

/**
 * Appends an OP_RETURN output (value 0) to a raw, unsigned hex transaction.
 * Returns the new transaction as a hex string. Any segwit witness data is
 * dropped because adding an output invalidates signatures anyway — the result
 * is a fresh unsigned transaction ready to re-import.
 */
export function addOpReturnToRawTx(rawHex: string, data: Buffer): string {
  const hex = normalizeHex(rawHex);
  if (hex.length === 0 || hex.length % 2 !== 0 || !/^[0-9a-f]+$/.test(hex)) {
    throw new PsbtError(
      "Invalid hex transaction. It should contain only 0-9 / a-f with an even length.",
    );
  }

  let buf: Buffer;
  try {
    buf = Buffer.from(hex, "hex");
  } catch {
    throw new PsbtError("Could not decode the hex transaction.");
  }

  let tx: ParsedTx;
  try {
    tx = parseTx(new Reader(buf));
  } catch {
    throw new PsbtError(
      "Could not parse this transaction. Make sure it is a complete raw transaction in hex.",
    );
  }

  // Append the OP_RETURN output.
  const script = buildOpReturnScript(data);
  tx.outputs = Buffer.concat([tx.outputs, serializeTxOutput(0n, script)]);
  tx.outputsCount += 1;

  // Drop witness data: changing outputs invalidates signatures, so we emit a
  // clean unsigned (non-witness) transaction that can be re-signed.
  tx.hasWitness = false;
  tx.witness = Buffer.alloc(0);

  return serializeTx(tx).toString("hex");
}

/**
 * Unified entry point: accepts either a Base64 PSBT or a raw hex transaction,
 * adds the OP_RETURN, and returns the result in the SAME format it was given.
 */
export function addOpReturn(
  input: string,
  data: Buffer,
): { output: string; format: TxInputFormat } {
  const format = detectFormat(input);
  if (format === "psbt") {
    return { output: addOpReturnToPsbt(input, data), format };
  }
  if (format === "rawhex") {
    return { output: addOpReturnToRawTx(input, data), format };
  }
  throw new PsbtError(
    "Unrecognized format. Paste either a Base64 PSBT (starts with \"cHNidP…\") or a raw transaction in hex.",
  );
}

/**
 * Appends an OP_RETURN output (value 0) carrying `data` to the given base64 PSBT.
 * Returns the new base64 PSBT string.
 */
export function addOpReturnToPsbt(base64Psbt: string, data: Buffer): string {
  const input = base64Psbt.trim();
  let buf: Buffer;
  try {
    buf = Buffer.from(input, "base64");
  } catch {
    throw new PsbtError("Invalid Base64. Please copy the PSBT again.");
  }

  if (!buf.subarray(0, 5).equals(PSBT_MAGIC)) {
    throw new PsbtError(
      "This does not look like a valid PSBT (magic bytes missing). In Sparrow, export via File → Save PSBT → As Base64.",
    );
  }

  const r = new Reader(buf);
  r.readBytes(5); // magic

  // Global map
  const globalStart = r.pos;
  // We need to find the unsigned tx (key 0x00) and rebuild the global map.
  const globalEntries: { key: Buffer; value: Buffer }[] = [];
  for (;;) {
    const keyLen = r.readVarInt();
    if (keyLen === 0) break; // separator
    const key = r.readBytes(keyLen);
    const valLen = r.readVarInt();
    const value = r.readBytes(valLen);
    globalEntries.push({ key, value });
  }
  const globalEnd = r.pos;
  void globalStart;
  void globalEnd;

  const txEntryIndex = globalEntries.findIndex((e) => e.key[0] === 0x00);
  if (txEntryIndex === -1) {
    throw new PsbtError(
      "No unsigned transaction found in the PSBT. Add the OP_RETURN BEFORE signing in Sparrow.",
    );
  }

  // Parse the unsigned tx, append our output, reserialize.
  const txBuf = globalEntries[txEntryIndex].value;
  const tx = parseTx(new Reader(txBuf));

  const script = buildOpReturnScript(data);
  const newOutput = serializeTxOutput(0n, script);
  tx.outputs = Buffer.concat([tx.outputs, newOutput]);
  tx.outputsCount += 1;

  globalEntries[txEntryIndex] = {
    key: globalEntries[txEntryIndex].key,
    value: serializeTx(tx),
  };

  // Read input maps (count = number of tx inputs) and output maps.
  // Count inputs from the tx we just parsed.
  const inReader = new Reader(tx.inputs);
  const numInputs = inReader.readVarInt();

  const inputMaps: Buffer[] = [];
  for (let i = 0; i < numInputs; i++) {
    inputMaps.push(readKeyValueMap(r));
  }

  const outputMaps: Buffer[] = [];
  // original number of output maps = original outputsCount (before our append)
  const originalOutputCount = tx.outputsCount - 1;
  for (let i = 0; i < originalOutputCount; i++) {
    outputMaps.push(readKeyValueMap(r));
  }

  // Rebuild the PSBT.
  const parts: Buffer[] = [PSBT_MAGIC];

  // global map
  for (const e of globalEntries) {
    parts.push(writeVarInt(e.key.length), e.key, writeVarInt(e.value.length), e.value);
  }
  parts.push(Buffer.from([0x00])); // separator

  // input maps (already include their trailing separator)
  for (const m of inputMaps) parts.push(m);

  // output maps (already include their trailing separator)
  for (const m of outputMaps) parts.push(m);

  // our new, empty output map => just a separator
  parts.push(Buffer.from([0x00]));

  const out = Buffer.concat(parts);
  return out.toString("base64");
}
